import { patch } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

const approveRequestSchema = z.object({
  messageId: z.string(),
  isUpdateResponse: z.boolean(),
  reason: z.string().optional(),
});

const rejectRequestSchema = z.object({
  messageId: z.string(),
  isUpdateResponse: z.boolean(),
  reason: z.string().optional(),
  // Always true: tells BE to skip auto-NCNS so this FE can handle it explicitly
  skipAutoNoShow: z.literal(true),
});

interface FcmRequestActionParams {
  shiftId: string;
  messageId: string;
  isUpdateResponse: boolean;
  reason?: string;
}

interface FcmRequestActionContext {
  workerId?: string;
  workplaceId?: string;
  workplaceUserId?: string;
}

export function useApproveFcmRequest(
  context: FcmRequestActionContext,
  options: UseMutationOptions<void, Error, FcmRequestActionParams> = {}
) {
  return useMutation({
    mutationFn: async (params: FcmRequestActionParams) => {
      const { shiftId, messageId, isUpdateResponse, reason } = params;

      await patch({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/shifts/${shiftId}/facility-cancelled-me/approve`,
        data: {
          messageId,
          isUpdateResponse,
          reason,
        },
        requestSchema: approveRequestSchema,
        responseSchema: z.unknown(),
      });
    },
    onSuccess: (_data, params) => {
      logEvent(APP_V2_APP_EVENTS.FCM_REQUEST_CONFIRMATION_SUBMITTED, {
        trigger: params.isUpdateResponse ? "updateResponse" : "addConfirmation",
        shiftId: params.shiftId,
        workerId: context.workerId,
        workplaceId: context.workplaceId,
        workplaceUserId: context.workplaceUserId,
        eventName: "facility_cancelled_me_confirmation",
        action: "approve",
        reason: params.reason,
        success: true,
      });
    },
    onError: (_error, params) => {
      logEvent(APP_V2_APP_EVENTS.FCM_REQUEST_CONFIRMATION_SUBMITTED, {
        trigger: params.isUpdateResponse ? "updateResponse" : "addConfirmation",
        shiftId: params.shiftId,
        workerId: context.workerId,
        workplaceId: context.workplaceId,
        workplaceUserId: context.workplaceUserId,
        eventName: "facility_cancelled_me_confirmation",
        action: "approve",
        reason: params.reason,
        success: false,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.FCM_REQUEST_APPROVE_FAILURE,
      userSuccessMessage: "Thanks for your response!",
      userErrorMessage: "Failed to approve request",
    },
    ...options,
  });
}

export function useRejectFcmRequest(
  context: FcmRequestActionContext,
  options: UseMutationOptions<void, Error, FcmRequestActionParams> = {}
) {
  return useMutation({
    mutationFn: async (params: FcmRequestActionParams) => {
      const { shiftId, messageId, isUpdateResponse, reason } = params;

      await patch({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/shifts/${shiftId}/facility-cancelled-me/reject`,
        data: {
          messageId,
          isUpdateResponse,
          reason,
          skipAutoNoShow: true as const,
        },
        requestSchema: rejectRequestSchema,
        responseSchema: z.unknown(),
      });
    },
    onSuccess: (_data, params) => {
      logEvent(APP_V2_APP_EVENTS.FCM_REQUEST_CONFIRMATION_SUBMITTED, {
        trigger: params.isUpdateResponse ? "updateResponse" : "addConfirmation",
        shiftId: params.shiftId,
        workerId: context.workerId,
        workplaceId: context.workplaceId,
        workplaceUserId: context.workplaceUserId,
        eventName: "facility_cancelled_me_confirmation",
        action: "reject",
        reason: params.reason,
        success: true,
      });
    },
    onError: (_error, params) => {
      logEvent(APP_V2_APP_EVENTS.FCM_REQUEST_CONFIRMATION_SUBMITTED, {
        trigger: params.isUpdateResponse ? "updateResponse" : "addConfirmation",
        shiftId: params.shiftId,
        workerId: context.workerId,
        workplaceId: context.workplaceId,
        workplaceUserId: context.workplaceUserId,
        eventName: "facility_cancelled_me_confirmation",
        action: "reject",
        reason: params.reason,
        success: false,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.FCM_REQUEST_REJECT_FAILURE,
      userSuccessMessage: "Thanks for your response!",
      userErrorMessage: "Failed to reject request",
    },
    ...options,
  });
}
